import { clientAPISend } from '@1x2network/client_api_integration'
import { flow } from '@1x2network/flow'
import { isPaylineConfiguration } from '@1x2network/is_engine_communication'
import { RestartSetupResponse } from '@1x2network/is_engine_communication/lib/types/response/SetupResponse'
import { slotStore } from '@1x2network/slot-ui'
import { State } from '@1x2network/state_controller'
import { createNudgeEffect } from '@1x2network/tweens'
import { produce } from 'immer'
import { collectorIds, valueIds } from '../../constants/valueIds'
import { BaseGameToHoldAndWinModel, CoinShowerEvent } from '../../model/BaseGameModel'
import { AnyHoldAndWinModel, CollectionEvent, HoldAndWinToBaseModel } from '../../model/HoldAndWinModel'
import { RestartSetupModel, SetupModel } from '../../model/SetupModel'
import { SpinModel } from '../../model/SpinModel'
import { applyHoldAndWinTransition } from '../../utils/applyBoardTransforms'
import { applyFinalValues } from '../../utils/applyFinalValues'
import { GameViews } from '../../views/GameViews'
import { initWinLines } from './initWinLines'

/**
 * Controller for the game in H&W state
 * @author Jordan Harrison - <jordan.harrison@1x2network.com>
 * @version 1.0.0
 */
export class HoldAndWinState extends State<
    GameViews,
    AnyHoldAndWinModel | BaseGameToHoldAndWinModel,
    RestartSetupResponse<AnyHoldAndWinModel>
> {
    private restarting = false
    private restarted = false

    public prepareState(model: BaseGameToHoldAndWinModel | RestartSetupModel<AnyHoldAndWinModel>): void {
        const { hawReelSet, middleReel, hwBackground, reelSet, baseBackground, hawCounter, excitement } = this.views
        const slotState = slotStore.getState()
        slotState.console.setSpinning()
        slotState.slamStop.setEnabled(false)

        baseBackground.visible = false
        hwBackground.visible = true
        hwBackground.reset()
        hwBackground.playIdle()
        hawReelSet.visible = true
        hawReelSet.getTopContainer(0).visible = true
        hawReelSet.getTopContainer(1).visible = true
        hawReelSet.getTopContainer(2).visible = true
        hawReelSet.addChild(middleReel.backgroundSpine, middleReel.foregroundSpine)
        this.views.coinShower.visible = false

        reelSet.visible = false
        reelSet.topContainer.visible = false

        excitement.pause()
        excitement.setState('holdandwin')

        hawCounter.setRemaining(model.state.holdAndWin.spins.remaining)

        const totalBoardValue = model.results.board.reels[2].reduce((acc, symbol) => {
            const cash = 'cash' in symbol ? symbol.cash.end.timesStake : 0
            return acc + cash
        }, 0)

        if (totalBoardValue >= 80) {
            this.views.excitement.setMaxHype()
            this.views.falsePersistence.animateMax()
        } else {
            this.views.falsePersistence.setInitialBonus()
        }
    }

    public handshake(model: RestartSetupModel<AnyHoldAndWinModel>): void {
        const { middleReel, hawReelSet, reelSet, transition, eventManager } = this.views

        eventManager.setEvents(model.results.wins.events)
        transition.display()

        this.restarting = true
        // Mutate the board to apply the hold and win transition and coin shower
        const reels = model.results.board.reels
        const transformEvent = eventManager.peek()?.type === 'TRANSFORM'
        const transformedSymbols = applyHoldAndWinTransition(
            reels,
            transformEvent ? (eventManager.nextEvent() as CoinShowerEvent) : undefined,
        )
        const mutatedSymbols = applyFinalValues(transformedSymbols)

        // Make blanks invisible on middle reel
        for (const symbol of mutatedSymbols[2]) {
            if (symbol.id === 15) {
                symbol.id = -1
            }
        }

        this.views.hawReelSet.prepareSpin(mutatedSymbols)
        middleReel.show()

        const landedPositions = hawReelSet.fetcher.getPositions(collectorIds)
        hawReelSet.lockCells(landedPositions)

        if (isPaylineConfiguration(model.config)) {
            initWinLines(reelSet, this.views.winLines, model.config.lines)
        }

        // Update jackpot widget values
        this.views.jackpotWidgets.updateValues(model.config.prizes)
    }

    public async introduceState(model: RestartSetupModel<AnyHoldAndWinModel>): Promise<void> {
        const slotState = slotStore.getState()
        slotState.message.setMessage('action-required')
        clientAPISend.actionRequired(true)

        const totalBoardValue = model.results.board.reels[2].reduce((acc, symbol) => {
            const cash = 'cash' in symbol ? symbol.cash.end.timesStake : 0
            return acc + cash
        }, 0)

        if (totalBoardValue < 80) {
            this.views.falsePersistence.animateBonusEnter()
        }

        await flow.wait(this.views.clickHandler.onClick)
        await this.views.transition.transitionOut()
        slotState.message.clearMessage()
        clientAPISend.actionRequired(false)

        slotState.message.setMessage('press-spin-to-continue')
    }

    public inState(model: SpinModel | SetupModel): boolean {
        return (
            model.state.phase.next === 'HOLD_AND_WIN' ||
            (model.state.phase.current === 'HOLD_AND_WIN' && model.state.phase.next === '')
        )
    }

    public async waitForInput(interact: Promise<void>): Promise<object | void> {
        if (this.restarting) {
            const storeState = slotStore.getState()
            storeState.console.setRestart()
            await interact
            storeState.message.clearMessage()
            this.restarting = false
            storeState.console.setSpinning()
            this.restarted = true
        }
        this.views.jackpotWidgets.reset()
    }

    public async waitForRound() {
        if (this.restarted) {
            clientAPISend.feature.start()
            this.restarted = false
        }
        clientAPISend.play.start()
        await Promise.all([this.views.hawCounter.decrease(), this.views.hawReelSet.startSpin()])
    }

    public async leaveState(model: HoldAndWinToBaseModel): Promise<void> {
        const isCapped = model.state.win.isCapped
        const { soundPlayer, middleReel } = this.views

        if (!isCapped) {
            await this.views.holdAndWinCountUp.animate(model.results.board.reels)
        }

        await this.handlePopup(model)

        if (isCapped) {
            slotStore.getState().message.setMessage('max-win')
            clientAPISend.actionRequired(true)
            await this.views.maxWinPopup.animate(model.state.win.total.currency)
            clientAPISend.actionRequired(false)
        }

        soundPlayer.fadeBackground(0.5)
        soundPlayer.playTransitionIn()
        soundPlayer.playHawEnd()

        await this.views.transition.transitionIn()
        middleReel.out()

        // we are transitioning to base game remove cash values
        this.views.reelSet.prepareSpin(applyFinalValues(model.results.board.reels))
    }

    public async enterState(model: BaseGameToHoldAndWinModel): Promise<void> {
        const { middleReel, hawReelSet, transition, soundPlayer } = this.views

        const landedPositions = hawReelSet.fetcher.getPositions(collectorIds)
        hawReelSet.lockCells(landedPositions)

        slotStore.getState().message.setMessage('action-required')
        clientAPISend.actionRequired(true)
        await flow.wait(this.views.clickHandler.onClick)
        clientAPISend.actionRequired(false)

        soundPlayer.playTransitionOut()
        middleReel.show()
        await transition.transitionOut()
        this.views.falsePersistence.animateBonusEnter()
        slotStore.getState().message.setMessage('good-luck')

        await this.handleAllCollectionEvents()
    }

    async playRound(model: AnyHoldAndWinModel): Promise<void> {
        const { hawReelSet, eventManager } = this.views
        eventManager.setEvents(model.results.wins.events)

        let valueSymbolLandedThisSpin = false
        let counterResetPromise = Promise.resolve()
        const sub = this.views.hawReelSet.spinAnimator.signals.onLand.connect((index) => {
            const reel = index % 5
            const row = Math.floor(index / 5)
            const symbol = hawReelSet.fetcher.getSymbolByPosition([reel, row])

            if (valueIds.includes(symbol.id)) {
                // Play landing animation
                symbol.playLanding()

                if (!valueSymbolLandedThisSpin) {
                    counterResetPromise = this.views.hawCounter.animateChange(model.state.holdAndWin.spins.remaining)
                }
                valueSymbolLandedThisSpin = true

                // Nudge the reel
                this.views.effectManager.addAnimation(
                    hawReelSet,
                    createNudgeEffect({ targetOffset: { y: 3 }, duration: 0.18 }),
                )
            }
            this.views.jackpotWidgets.displayLanded([symbol.id])
        })

        const symbols = produce(model.results.board.reels, (draft) => {
            for (const symbol of draft[2]) {
                if (symbol.id === 15) {
                    symbol.id = -1
                }
            }
        })

        await hawReelSet.stopSpin({
            symbols,
            minDuration: 1500,
        })

        const totalReelValue = model.results.board.reels[2].reduce((acc, symbol) => {
            const cash = 'cash' in symbol ? symbol.cash.end.timesStake : 0
            return acc + cash
        }, 0)

        if (totalReelValue >= 80) {
            this.views.excitement.setMaxHype()
            this.views.falsePersistence.animateMax()
        }

        sub.disconnect()
        const landedPositions = hawReelSet.fetcher.getPositions(collectorIds)
        hawReelSet.lockCells(landedPositions)

        if (!valueSymbolLandedThisSpin) {
            counterResetPromise = this.views.hawCounter.animateChange(model.state.holdAndWin.spins.remaining)
        }

        if (!eventManager.peek()) {
            await flow.wait(500)
        }

        // something has landed, lets celebrate
        if (valueSymbolLandedThisSpin) {
            this.views.pot.playWin()
        }
        await Promise.all([this.handleAllCollectionEvents(), counterResetPromise])
        clientAPISend.play.end()
    }

    private async handleAllCollectionEvents() {
        const { eventManager } = this.views
        const collectionEvents: CollectionEvent[] = []

        // gather all collection events that are firing
        while (eventManager.peek()?.type === 'CASH_COLLECTION') {
            collectionEvents.push(eventManager.nextEvent() as CollectionEvent)
        }

        // animate them all together
        await Promise.all(collectionEvents.map((event) => this.handleCollectionEvent(event as CollectionEvent)))
    }

    private async handleCollectionEvent(event: CollectionEvent): Promise<void> {
        const symbol = this.views.hawReelSet.fetcher.getSymbolByPosition(event.to)
        this.views.hawReelSet.getTopContainer(event.to[0]).addChild(symbol)
        await this.views.collection.animate(this.views.hawReelSet.fetcher, event)
    }

    /** animate one of the popups based on win */
    async handlePopup(model: HoldAndWinToBaseModel) {
        const win = model.state.win.total.currency
        const stake = model.bet.stake.currency
        await this.views.tieredPopup.animate(stake, win)
    }
}
